/*
 * Copyright (c) 2020. American Express Company
 */

// third-party
import _ from 'lodash';
import React, { Component, ReactComponentElement } from 'react';
import { LocalDate } from '@js-joda/core';

// models and services
import { RealEstateModel } from 'src/types/real-estate.model';
import CashFlowService from '../../services/cashflow.service';
import RealEstateService from '../../services/real-estate.service';

// components
import Dropdown from '../../components/menus/Dropdown';
import BackButton from '../../components/back-button/BackButton';
import BWCurrencyInput from 'src/bw/bw-currency-input/BW-Currency-Input';
import BWPercentInput from '../bw-percent-input/BW-Percent-Input';
import MonthPicker from 'src/bw/month-picker/MonthPicker';
import PlaidButton from './PlaidButton';
import { ButtonPrimary, ButtonSecondary, Input, Label } from '@americanexpress/dls-react';
import Typography from '../../components/typography/Typography';
import { TypographyVariants } from 'src/types/typography-variants.enum';

// styles
import { ZILLOW_INTEGRATION } from 'src/utils/constants';
import './RealEstateDrawer.scss';

export default class RealEstateDrawer extends Component<
    any,
    {
        asset: RealEstateModel;
    }
> {
    /// ////////////////
    // Onced Methods //
    /// ////////////////
    private readonly handleDeleteOnce: () => void = _.once(async () => await this.handleDelete());

    private readonly handleUpdateOnce: () => void = _.once(async () => await this.handleSave());

    public override state = {
        asset: null as RealEstateModel,
        zData: null as any
    };

    constructor(props: any) {
        super(props);
        this.state = { asset: null, zData: null };
    }

    public static getDerivedStateFromProps(props: any, prevState: any): any {
        let asset: RealEstateModel = null;

        if (!prevState.asset) {
            if (props.drawerProps.currentRealEstate) {
                asset = props.drawerProps.currentRealEstate;
            }
        } else {
            return prevState;
        }

        return { asset };
    }

    public override render(): ReactComponentElement<any> {
        const addOrUpdate: string = this.props.drawerProps.currentRealEstate ? 'Update Real Estate' : 'Add Real Estate';
        const addOrUpdateButton: string = this.props.drawerProps.currentRealEstate
            ? 'Update Real Estate'
            : 'Add Real Estate';

        const deleteButton = this.state.asset?.id ? (
            <ButtonSecondary
                className="bottom-buttons"
                onClick={() => {
                    this.props.handleOpenConfirmation({
                        onAction: this.handleDeleteOnce.bind(this),
                        action: 'delete',
                        object: 'manual'
                    });
                }}
                key="save"
            >
                Delete
            </ButtonSecondary>
        ) : null;

        const asset: RealEstateModel = this.state.asset;
        const hasMortgage: boolean = asset.mortgageType === 'mortgage_15' || asset.mortgageType === 'mortgage_30';

        const mortgage = hasMortgage ? (
            <div key="mortgage" className="after24">
                <Label htmlFor="loan-amount">Mortgage Loan Amount</Label>
                <BWCurrencyInput
                    fullWidth={true}
                    key="loan-amount"
                    keidy="loan-amount"
                    className="asset-mortgage"
                    defaultValue={0}
                    status=""
                    value={asset.mortgageAmount || undefined}
                    showFractions={false}
                    onChange={async (value: number) => await this.handleChange(value, 'mortgageAmount')}
                />
                <Typography variant={TypographyVariants.LEGAL_PRIMARY}>
                    The total amount borrowed, not your current balance.
                </Typography>
            </div>
        ) : null;

        const mortgageDate = hasMortgage ? (
            <div key="date" className="after24">
                <MonthPicker
                    date={asset.mortgageDate ? asset.mortgageDate : LocalDate.now()}
                    mode="any"
                    label="Mortgage Date"
                    endYear={CashFlowService.getLastCashFlow(this.props.user.cashFlow).startDate.year()}
                    handleChangeDate={async (date: LocalDate) => {
                        await this.handleChange(date, 'mortgageDate');
                    }}
                    whichDate="mortgageDate"
                />
            </div>
        ) : null;

        const mortgageInterest = hasMortgage ? (
            <div key="interest" className="after24">
                <BWPercentInput
                    fullWidth
                    type="string"
                    label="Interest"
                    placeholder="Rate (Percentage)"
                    className="asset-interest"
                    value={asset.mortgageInterest || undefined}
                    variant="outlined"
                    onChange={async (e: any) => await this.handleChange(e.target.value, 'mortgageInterest', 'percent')}
                />
            </div>
        ) : null;

        const mortgagePayment = hasMortgage ? (
            <div key="payment" className="after24">
                <Label htmlFor="monthly-payment">Monthly Payment</Label>
                <BWCurrencyInput
                    fullWidth={true}
                    id="monthly-payment"
                    key="monthly-payment"
                    className="asset-payment"
                    defaultValue={0}
                    status=""
                    value={asset.amount || 0}
                    showFractions={false}
                    onChange={async (value: number) => await this.handleChange(value, 'mortgagePayment', 'currency')}
                />
                <Typography variant={TypographyVariants.LEGAL_PRIMARY}>Principal & interest only</Typography>
            </div>
        ) : null;

        return (
            <div className="real-estate-drawer drawer">
                <BackButton drawerProps={this.props.drawerProps} handleBack={this.props.handleBack} />
                <Typography variant={TypographyVariants.HEADING_S} className="page-title">
                    {addOrUpdate}
                </Typography>
                <PlaidButton
                    handleClearFirstImport={this.props.handleClearFirstImport}
                    handleFirstImport={this.props.handleFirstImport}
                    handleReloadUser={this.props.handleReloadUser}
                    firstImport={this.props.firstImport}
                    setDrawer={this.props.setDrawer}
                    className="plaid-link"
                    user={this.props.user}
                    handleAccounts={this.props.handleAccounts}
                />

                <div key="address" className="after24 before24">
                    <Label htmlFor="asset-address">Address</Label>
                    <Input
                        id="asset-address"
                        type="string"
                        className="asset-address"
                        value={asset.address ? asset.address : ''}
                        variant="outlined"
                        onChange={async (e: any) => await this.handleChange(e.target.value, 'address')}
                    />
                </div>
                <div key="total" className="after24">
                    <Label htmlFor="monthly-payment">Current Home Value</Label>
                    <BWCurrencyInput
                        fullWidth={true}
                        id="asset-total"
                        className="asset-total"
                        defaultValue={0}
                        status=""
                        value={asset.amount || 0}
                        showFractions={false}
                        onChange={async (value: number) => await this.handleChange(value, 'amount')}
                    />
                    {ZILLOW_INTEGRATION ? (
                        <Typography variant={TypographyVariants.CAPTION}>
                            Value powered by&nbsp;
                            <a
                                href={
                                    _.isEmpty(this.state.zData)
                                        ? 'https://www.zillow.com'
                                        : _.get(this.state.zData, 'links[0].homedetails[0]')
                                }
                                rel="noopener noreferrer"
                                target="_blank"
                            >
                                Zillow
                            </a>
                        </Typography>
                    ) : null}
                </div>
                <div key="mortgage_type" className="after24">
                    <Label>Mortgage Type</Label>
                    <Dropdown
                        value={asset.mortgageType}
                        onChange={async (e) => {
                            await this.handleChange(e.target.value, 'mortgageType');
                        }}
                    >
                        <option key="mortgage_none" value="mortgage_none" />
                        <option key="mortgage_15" value="mortgage_15">
                            15 year
                        </option>
                        <option key="mortgage_30" value="mortgage_30">
                            30 year
                        </option>
                    </Dropdown>
                    <Typography variant={TypographyVariants.CAPTION} component="div">
                        Optional
                    </Typography>
                </div>

                {mortgage}
                {mortgageDate}
                {mortgageInterest}
                {mortgagePayment}
                <div key="buttons" className="bottom-buttons">
                    <ButtonPrimary onClick={this.handleUpdateOnce.bind(this)}>{addOrUpdateButton}</ButtonPrimary>
                    {deleteButton}
                </div>
            </div>
        );
    }

    private async handleChange(value: any, name: string, type: string = 'none'): Promise<void> {
        const asset: RealEstateModel = this.state.asset;

        switch (type) {
            case 'currency':
            case 'percent':
                (asset as any)[name] = parseInt(value.replace(/[^\d.]/g, ''));
                break;
            default:
                (asset as any)[name] = value;
        }

        this.setState({
            asset
        });
    }

    private async handleDelete(): Promise<void> {
        await RealEstateService.deleteRealEstate(this.state.asset);

        this.setState({
            asset: null
        });

        this.goBack();
        this.props.handleReloadUser();
    }

    private async handleSave(): Promise<void> {
        if (this.state.asset.id) {
            await RealEstateService.updateRealEstate(this.state.asset);
        } else {
            await RealEstateService.createRealEstate(this.state.asset);
        }

        this.setState({
            asset: null
        });

        this.goBack();
        this.props.handleReloadUser();
    }

    private goBack(): void {
        if (_.get(this, 'props.user.attributes.startupStep')) {
            this.props.handleTopDrawer();
        } else {
            this.props.handleAccounts();
        }
    }
}
